import { useState, useEffect, memo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSession } from "@/lib/auth-client";

interface Tag {
  id: string;
  status: string; // 'unused' | 'active' | 'used'
  reusable: number;
}

interface Fudge {
  id: string;
  description: string;
  changeDetail: string;
  location: string | null;
  kks: string | null;
  appliedByName: string;
  appliedAt: string;
  reinstatedByName: string | null;
  reinstatedAt: string | null;
}

interface KKSCode { id: string; code: string; description: string; }

type Screen =
  | "loading"
  | "register"
  | "reinstate"
  | "confirm"
  | "expired"
  | "reuse"
  | "success-reg"
  | "success-rei"
  | "error";

export default function ScanPage() {
  const { id: tagId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = useSession();
  const [screen, setScreen] = useState<Screen>("loading");
  const [tag, setTag] = useState<Tag | null>(null);
  const [fudge, setFudge] = useState<Fudge | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Register / reuse form
  const [description, setDescription] = useState("");
  const [changeDetail, setChangeDetail] = useState("");
  const [location, setLocation] = useState("");
  const [appliedByName, setAppliedByName] = useState("");

  // KKS sub-fields
  const [kksOpen, setKksOpen] = useState(false);
  const [kksStation, setKksStation] = useState("");
  const [kksUnit, setKksUnit] = useState("");
  const [kksSystemCode, setKksSystemCode] = useState("");
  const [kksSystemNumber, setKksSystemNumber] = useState("");
  const [kksEquipUnitCode, setKksEquipUnitCode] = useState("");
  const [kksEquipNumber, setKksEquipNumber] = useState("");
  const [kksComponentCode, setKksComponentCode] = useState("");
  const [kksComponentNumber, setKksComponentNumber] = useState("");
  const [systemCodes, setSystemCodes] = useState<KKSCode[]>([]);
  const [equipUnitCodes, setEquipUnitCodes] = useState<KKSCode[]>([]);
  const [componentCodes, setComponentCodes] = useState<KKSCode[]>([]);

  // Reinstate form
  const [reinstatedByName, setReinstatedByName] = useState("");

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!sessionPending && !session) {
      navigate(`/?redirect=/scan/${tagId}`, { replace: true });
    }
  }, [sessionPending, session, tagId, navigate]);

  // Countdown and auto-redirect after success
  const [countdown, setCountdown] = useState(5);
  useEffect(() => {
    if (screen !== "success-reg" && screen !== "success-rei" && screen !== "expired") return;
    setCountdown(2);
    const interval = setInterval(() => {
      setCountdown((n) => {
        if (n <= 1) { clearInterval(interval); navigate("/dashboard"); }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [screen, navigate]);

  // Pre-fill name fields from logged-in session
  useEffect(() => {
    if (session?.user?.name) {
      setAppliedByName(session.user.name);
      setReinstatedByName(session.user.name);
    }
  }, [session?.user?.name]);

  // Load KKS lookup tables and site defaults once
  const loadKksData = useCallback(async () => {
    const [sc, eu, cc, cfg] = await Promise.all([
      fetch("/api/kks/system-codes").then((r) => r.ok ? r.json() : []),
      fetch("/api/kks/equipment-unit-codes").then((r) => r.ok ? r.json() : []),
      fetch("/api/kks/component-codes").then((r) => r.ok ? r.json() : []),
      fetch("/api/kks/config").then((r) => r.ok ? r.json() : null),
    ]);
    setSystemCodes(sc);
    setEquipUnitCodes(eu);
    setComponentCodes(cc);
    // Prefer last non-blank value the engineer used; fall back to site default
    const lastStation = localStorage.getItem("kks_last_station") || "";
    const lastUnit = localStorage.getItem("kks_last_unit") || "";
    setKksStation(lastStation || cfg?.station || "");
    setKksUnit(lastUnit || cfg?.unit || "");
  }, []);

  useEffect(() => { loadKksData(); }, [loadKksData]);

  // Apply re-apply prefill from history if present
  useEffect(() => {
    if (screen !== "register" && screen !== "reuse") return;
    const raw = localStorage.getItem("fudge_prefill");
    if (!raw) return;
    const p = JSON.parse(raw);
    if (p.expiresAt > Date.now()) {
      setDescription(p.description ?? "");
      setChangeDetail(p.changeDetail ?? "");
      setLocation(p.location ?? "");
      if (p.kks) {
        // Pre-fill KKS and open the section
        setKksOpen(true);
        // Store assembled string; user can see it in the preview
        // We set it via a single assembled approach
        setKksStation(""); setKksUnit("");
        setKksSystemCode(""); setKksSystemNumber("");
        setKksEquipUnitCode(""); setKksEquipNumber("");
        setKksComponentCode(""); setKksComponentNumber("");
        // parse the existing kks string back into fields if possible
        const kks = p.kks as string;
        // Format: SSS U CCC NN EE EEE - CC NN
        // e.g. THI1BBA01AP001-EY01
        const m = kks.match(/^([A-Z]{3})([0-9])([A-Z]{3})([0-9]{2})([A-Z]{2})([0-9]{3})(?:-([A-Z]{2})([0-9]{2}))?$/);
        if (m) {
          setKksStation(m[1]); setKksUnit(m[2]);
          setKksSystemCode(m[3]); setKksSystemNumber(m[4]);
          setKksEquipUnitCode(m[5]); setKksEquipNumber(m[6]);
          if (m[7]) { setKksComponentCode(m[7]); setKksComponentNumber(m[8]); }
        }
      }
    }
    localStorage.removeItem("fudge_prefill");
  }, [screen]);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!tagId) { setScreen("error"); setErrorMsg("No tag ID in URL."); return; }

    fetch(`/api/tags/${tagId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) { setScreen("error"); setErrorMsg(data.error); return; }
        setTag(data.tag);
        setFudge(data.fudge);
        if (data.tag.status === "unused") setScreen("register");
        else if (data.tag.status === "active") setScreen("reinstate");
        else if (data.tag.status === "used" && data.tag.reusable) setScreen("reuse");
        else setScreen("expired");
      })
      .catch(() => { setScreen("error"); setErrorMsg("Could not contact the server."); });
  }, [tagId]);

  function assembledKks(): string | null {
    if (!kksStation || !kksUnit || !kksSystemCode || !kksSystemNumber || !kksEquipUnitCode || !kksEquipNumber) return null;
    let kks = `${kksStation}${kksUnit}${kksSystemCode}${kksSystemNumber}${kksEquipUnitCode}${kksEquipNumber}`;
    if (kksComponentCode && kksComponentNumber) kks += `-${kksComponentCode}${kksComponentNumber}`;
    return kks;
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const kks = kksOpen ? assembledKks() : null;
    const r = await fetch("/api/fudges", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tagId, description, changeDetail, location, appliedByName, kks }),
    });
    setSubmitting(false);
    if (r.ok) {
      if (kksOpen) {
        if (kksStation) localStorage.setItem("kks_last_station", kksStation);
        if (kksUnit) localStorage.setItem("kks_last_unit", kksUnit);
      }
      setScreen("success-reg");
    } else { const j = await r.json(); setErrorMsg(j.error ?? "Failed."); setScreen("error"); }
  }

  async function handleReinstate() {
    setSubmitting(true);
    const r = await fetch(`/api/fudges/${fudge!.id}/reinstate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reinstatedByName }),
    });
    setSubmitting(false);
    if (r.ok) setScreen("success-rei");
    else { const j = await r.json(); setErrorMsg(j.error ?? "Failed."); setScreen("error"); }
  }

  function fmt(iso: string) {
    return new Date(iso).toLocaleString();
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center pt-8 px-4 pb-8">
      <div className="w-full max-w-md">
        <p className="text-center text-xs text-slate-400 mb-4 font-mono">
          FudgeDatabase · tag {tagId?.slice(0, 8)}…
        </p>

        {/* Loading */}
        {screen === "loading" && (
          <Card>
            <p className="text-center text-slate-400 text-sm py-8">Loading…</p>
          </Card>
        )}

        {/* Error */}
        {screen === "error" && (
          <Card>
            <div className="text-center py-4">
              <p className="text-4xl mb-3">⚠️</p>
              <p className="font-semibold text-slate-800 mb-1">Something went wrong</p>
              <p className="text-sm text-slate-500">{errorMsg}</p>
            </div>
          </Card>
        )}

        {/* Expired tag */}
        {screen === "expired" && (
          <Card>
            <div className="text-center mb-5">
              <p className="text-4xl mb-3">⚠️</p>
              <h1 className="text-xl font-bold text-slate-900">This tag has already been used</h1>
            </div>
            {fudge && (
              <div className="bg-slate-50 rounded-lg p-4 mb-5 text-sm space-y-1.5">
                <p className="font-medium text-slate-700">{fudge.description}</p>
                <p className="text-slate-500">{fudge.changeDetail}</p>
                {fudge.location && <p className="text-slate-400 text-xs">📍 {fudge.location}</p>}
                {fudge.reinstatedAt && (
                  <p className="text-slate-400 text-xs">Reinstated {fmt(fudge.reinstatedAt)}</p>
                )}
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center mb-4">
              <p className="text-sm font-medium text-amber-800">
                Discard this tag and use a fresh one from your case.
              </p>
            </div>
            <Countdown seconds={countdown} onClick={() => navigate("/dashboard")} />
          </Card>
        )}

        {/* Reuse (reusable tag, status: used) */}
        {screen === "reuse" && (
          <Card>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5 text-sm text-amber-800">
              <p className="font-medium mb-1">This tag has been used before</p>
              {fudge && <p className="text-xs text-amber-700">{fudge.description}</p>}
              <p className="mt-2 text-xs">Reusable tags are enabled — you can register a new fudge on this tag.</p>
            </div>
            <form onSubmit={handleRegister} className="space-y-4">
              <Field label="Description — why is this fudge needed? *" value={description} onChange={setDescription} required />
              <Field label="Change Detail — what was physically done? *" value={changeDetail} onChange={setChangeDetail} required />
              <Field label="Location / Panel Reference *" value={location} onChange={setLocation} required />
              <KksSection
                open={kksOpen} onToggle={() => setKksOpen((v) => !v)}
                station={kksStation} setStation={setKksStation}
                unit={kksUnit} setUnit={setKksUnit}
                systemCode={kksSystemCode} setSystemCode={setKksSystemCode}
                systemNumber={kksSystemNumber} setSystemNumber={setKksSystemNumber}
                equipUnitCode={kksEquipUnitCode} setEquipUnitCode={setKksEquipUnitCode}
                equipNumber={kksEquipNumber} setEquipNumber={setKksEquipNumber}
                componentCode={kksComponentCode} setComponentCode={setKksComponentCode}
                componentNumber={kksComponentNumber} setComponentNumber={setKksComponentNumber}
                systemCodes={systemCodes} equipUnitCodes={equipUnitCodes} componentCodes={componentCodes}
                assembled={kksOpen ? assembledKks() : null}
              />
              <Field label="Your Name *" value={appliedByName} onChange={setAppliedByName} required />
              <BigButton type="submit" disabled={submitting} color="blue">
                {submitting ? "Registering…" : "Register Fudge"}
              </BigButton>
            </form>
          </Card>
        )}

        {/* Register new fudge */}
        {screen === "register" && (
          <Card>
            <div className="mb-6 text-center">
              <p className="text-4xl mb-2">📋</p>
              <h1 className="text-xl font-bold text-slate-900">Register a Fudge</h1>
              <p className="text-sm text-slate-500 mt-1">Fresh tag — fill in the change details</p>
            </div>
            <form onSubmit={handleRegister} className="space-y-4">
              <Field label="Description — why is this fudge needed? *" value={description} onChange={setDescription} required />
              <Field label="Change Detail — what was physically done? *" value={changeDetail} onChange={setChangeDetail} required />
              <Field label="Location / Panel Reference *" value={location} onChange={setLocation} required />
              <KksSection
                open={kksOpen} onToggle={() => setKksOpen((v) => !v)}
                station={kksStation} setStation={setKksStation}
                unit={kksUnit} setUnit={setKksUnit}
                systemCode={kksSystemCode} setSystemCode={setKksSystemCode}
                systemNumber={kksSystemNumber} setSystemNumber={setKksSystemNumber}
                equipUnitCode={kksEquipUnitCode} setEquipUnitCode={setKksEquipUnitCode}
                equipNumber={kksEquipNumber} setEquipNumber={setKksEquipNumber}
                componentCode={kksComponentCode} setComponentCode={setKksComponentCode}
                componentNumber={kksComponentNumber} setComponentNumber={setKksComponentNumber}
                systemCodes={systemCodes} equipUnitCodes={equipUnitCodes} componentCodes={componentCodes}
                assembled={kksOpen ? assembledKks() : null}
              />
              <Field label="Your Name *" value={appliedByName} onChange={setAppliedByName} required />
              <BigButton type="submit" disabled={submitting} color="blue">
                {submitting ? "Registering…" : "Register Fudge"}
              </BigButton>
            </form>
          </Card>
        )}

        {/* Reinstate — show fudge + name entry */}
        {screen === "reinstate" && fudge && (
          <Card>
            <div className="mb-5 text-center">
              <p className="text-4xl mb-2">🔧</p>
              <h1 className="text-xl font-bold text-slate-900">Active Fudge</h1>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 mb-5 space-y-2 text-sm">
              <p className="font-semibold text-slate-800">{fudge.description}</p>
              <p className="text-slate-600">{fudge.changeDetail}</p>
              {fudge.location && <p className="text-slate-400 text-xs">📍 {fudge.location}</p>}
              <p className="text-slate-400 text-xs">Applied by {fudge.appliedByName} · {fmt(fudge.appliedAt)}</p>
            </div>
            <div className="space-y-4">
              <Field label="Your Name *" value={reinstatedByName} onChange={setReinstatedByName} required />
              <BigButton
                type="button"
                disabled={!reinstatedByName.trim()}
                color="green"
                onClick={() => setScreen("confirm")}
              >
                Reinstate Fudge
              </BigButton>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full py-3 text-slate-500 text-sm hover:text-slate-700"
              >
                Keep Fudge — go to dashboard
              </button>
            </div>
          </Card>
        )}

        {/* Confirmation dialog */}
        {screen === "confirm" && (
          <Card>
            <div className="text-center mb-6">
              <p className="text-4xl mb-3">⚠️</p>
              <h2 className="text-lg font-bold text-slate-900 mb-2">Are you sure?</h2>
              <p className="text-sm text-slate-600">
                This tag will be spent — you will need a fresh tag to register a new fudge here.
              </p>
            </div>
            <div className="space-y-3">
              <BigButton type="button" color="green" disabled={submitting} onClick={handleReinstate}>
                {submitting ? "Reinstating…" : "Yes, Reinstate"}
              </BigButton>
              <button
                onClick={() => setScreen("reinstate")}
                className="w-full py-3 text-slate-500 text-sm hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          </Card>
        )}

        {/* Success — registered */}
        {screen === "success-reg" && (
          <Card>
            <div className="text-center py-4">
              <p className="text-5xl mb-3">✅</p>
              <h1 className="text-xl font-bold text-slate-900 mb-2">Fudge Registered</h1>
              <p className="text-sm text-slate-500 mb-4">The change has been recorded.</p>
              <Countdown seconds={countdown} onClick={() => navigate("/dashboard")} />
            </div>
          </Card>
        )}

        {/* Success — reinstated */}
        {screen === "success-rei" && (
          <Card>
            <div className="text-center py-2">
              <p className="text-5xl mb-3">✅</p>
              <h1 className="text-xl font-bold text-slate-900 mb-2">Fudge Reinstated</h1>
              <p className="text-sm text-slate-500 mb-4">The change has been reversed and recorded.</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-amber-800">Remove and discard the tag.</p>
              </div>
              <Countdown seconds={countdown} onClick={() => navigate("/dashboard")} />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// Shared sub-components

function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-white rounded-xl p-8 shadow-sm">{children}</div>;
}

// Memoised so React never re-renders it — keeps the CSS animation running smoothly
const CountdownBar = memo(() => (
  <>
    <style>{`@keyframes bar-shrink { from { width: 100%; } to { width: 0%; } }`}</style>
    <div className="w-full bg-slate-100 rounded-full h-2 mb-2 overflow-hidden">
      <div className="bg-blue-600 h-2 rounded-full" style={{ animation: "bar-shrink 2s linear forwards" }} />
    </div>
  </>
));

function Countdown({ seconds, onClick }: { seconds: number; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full text-center group">
      <CountdownBar />
      <span className="text-xs text-slate-400 group-hover:text-slate-600">
        Returning to dashboard in {seconds}s — tap to go now
      </span>
    </button>
  );
}

function Field({
  label, value, onChange, required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="text"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}

function BigButton({
  children, color, disabled, type, onClick,
}: {
  children: React.ReactNode;
  color: "blue" | "green";
  disabled?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
}) {
  const bg = color === "green"
    ? "bg-green-600 hover:bg-green-700"
    : "bg-blue-600 hover:bg-blue-700";
  return (
    <button
      type={type ?? "button"}
      disabled={disabled}
      onClick={onClick}
      className={`w-full py-4 text-white font-semibold rounded-xl text-lg ${bg} disabled:opacity-40 transition-colors`}
    >
      {children}
    </button>
  );
}

// ── KKS custom dropdown — shows CODE in box, CODE — Description in list ──────

function KksSelect({
  value, onChange, options, placeholder = "Select…",
}: {
  value: string;
  onChange: (v: string) => void;
  options: KKSCode[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent | TouchEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); };
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className={value ? "font-mono font-medium text-slate-800" : "text-slate-400"}>
          {value || placeholder}
        </span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
          style={{ width: 14, height: 14, flexShrink: 0, color: "#94a3b8", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          <button type="button" onClick={() => { onChange(""); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:bg-slate-50 border-b border-slate-100">
            {placeholder}
          </button>
          {options.map((c) => (
            <button key={c.id} type="button"
              onClick={() => { onChange(c.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 ${value === c.code ? "bg-blue-50" : ""}`}
            >
              <span className={`font-mono font-semibold ${value === c.code ? "text-blue-700" : "text-slate-800"}`}>{c.code}</span>
              <span className="text-slate-400 text-xs"> — {c.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── KKS entry section ─────────────────────────────────────────────────────────

function KksSection({
  open, onToggle,
  station, setStation,
  unit, setUnit,
  systemCode, setSystemCode,
  systemNumber, setSystemNumber,
  equipUnitCode, setEquipUnitCode,
  equipNumber, setEquipNumber,
  componentCode, setComponentCode,
  componentNumber, setComponentNumber,
  systemCodes, equipUnitCodes, componentCodes,
  assembled,
}: {
  open: boolean; onToggle: () => void;
  station: string; setStation: (v: string) => void;
  unit: string; setUnit: (v: string) => void;
  systemCode: string; setSystemCode: (v: string) => void;
  systemNumber: string; setSystemNumber: (v: string) => void;
  equipUnitCode: string; setEquipUnitCode: (v: string) => void;
  equipNumber: string; setEquipNumber: (v: string) => void;
  componentCode: string; setComponentCode: (v: string) => void;
  componentNumber: string; setComponentNumber: (v: string) => void;
  systemCodes: KKSCode[]; equipUnitCodes: KKSCode[]; componentCodes: KKSCode[];
  assembled: string | null;
}) {
  const inputCls = "px-2 py-2 border border-slate-300 rounded-lg text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-xs font-medium text-slate-500 mb-1";

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
      >
        <div>
          <span className="text-sm font-medium text-slate-700">KKS Code</span>
          <span className="ml-2 text-xs text-slate-400">(optional — for DCS tags)</span>
        </div>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          strokeLinecap="round" strokeLinejoin="round"
          style={{ width: 16, height: 16, flexShrink: 0, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="px-4 pt-3 pb-4 space-y-3">
          {/* Row 1: Station (flex-1) + Unit (narrow) */}
          <div className="flex items-end gap-2">
            <div className="flex-1" style={{ minWidth: 0 }}>
              <label className={labelCls}>Station</label>
              <input value={station} maxLength={3}
                onChange={(e) => setStation(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3))}
                placeholder="_ _ _" className={`${inputCls} w-full`} />
            </div>
            <div style={{ width: '3rem', flexShrink: 0 }}>
              <label className={labelCls}>Unit</label>
              <input value={unit} maxLength={1}
                onChange={(e) => setUnit(e.target.value.replace(/[^0-9]/g, "").slice(0, 1))}
                placeholder="_" className={`${inputCls} w-full`} />
            </div>
          </div>

          {/* Row 2: System Code (flex-1) + System No. (narrow) */}
          <div className="flex items-end gap-2">
            <div className="flex-1" style={{ minWidth: 0 }}>
              <label className={labelCls}>System Code</label>
              <KksSelect value={systemCode} onChange={setSystemCode} options={systemCodes} />
            </div>
            <div style={{ width: '4rem', flexShrink: 0 }}>
              <label className={labelCls}>Sys No.</label>
              <input value={systemNumber} maxLength={2}
                onChange={(e) => setSystemNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                placeholder="_ _" className={`${inputCls} w-full`} />
            </div>
          </div>

          {/* Row 3: Equipment Unit Code (flex-1) + Equipment No. (narrow) */}
          <div className="flex items-end gap-2">
            <div className="flex-1" style={{ minWidth: 0 }}>
              <label className={labelCls}>Equipment Unit Code</label>
              <KksSelect value={equipUnitCode} onChange={setEquipUnitCode} options={equipUnitCodes} />
            </div>
            <div style={{ width: '4.5rem', flexShrink: 0 }}>
              <label className={labelCls}>Equip No.</label>
              <input value={equipNumber} maxLength={3}
                onChange={(e) => setEquipNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                placeholder="_ _ _" className={`${inputCls} w-full`} />
            </div>
          </div>

          {/* Row 4: Component Code (flex-1) + Component No. (narrow, optional) */}
          <div className="flex items-end gap-2">
            <div className="flex-1" style={{ minWidth: 0 }}>
              <label className={labelCls}>Component Code <span className="text-slate-400">(optional)</span></label>
              <KksSelect
                value={componentCode}
                onChange={(v) => { setComponentCode(v); if (!v) setComponentNumber(""); }}
                options={componentCodes}
                placeholder="None"
              />
            </div>
            <div style={{ width: '4rem', flexShrink: 0 }}>
              <label className={labelCls}>Comp No.</label>
              <input value={componentNumber} maxLength={2} disabled={!componentCode}
                onChange={(e) => setComponentNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                placeholder="_ _"
                className={`${inputCls} w-full disabled:opacity-40 disabled:bg-slate-50`} />
            </div>
          </div>

          {/* Live preview */}
          {assembled ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <p className="text-xs text-blue-600 font-medium mb-0.5">KKS Preview</p>
              <p className="text-sm font-mono font-semibold text-blue-800">{assembled}</p>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">
              Fill Station, Unit, System Code, System No., Equipment Unit Code and Equipment No. to assemble the KKS.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
